import { useApi } from '../../lib/useApi.js';
import './access.redesign.css';

// Roles that trigger division-scoped viewing (PfM / PgM). When one is selected
// the requester is asked which division they will lead, and the request pins the
// /loe-dashboard landing so provisioning lands them on the division board.
const DIVISION_ROLE_IDS = new Set(['ROLE-PORTFOLIO-MANAGER', 'ROLE-PROGRAM-MANAGER']);

// Strategic executive / execution orgs are not selectable division homes — a
// division lead scopes to an operating division, not a strategic office.
const STRATEGIC_ORG_PATTERN = /strategic (executive|execution)/i;

const RISK_TONE = { standard: 'sage', elevated: 'amber', high: 'brick' };
const RISK_OPTIONS = [
  { value: 'standard', sub: 'Routine scope' },
  { value: 'elevated', sub: 'Added scrutiny' },
  { value: 'high', sub: 'Full review' },
];

export function rolesTriggerDivision(roleIds = []) {
  return (Array.isArray(roleIds) ? roleIds : []).some((id) => DIVISION_ROLE_IDS.has(id));
}

function toggleRole(roleIds, id) {
  const list = Array.isArray(roleIds) ? roleIds : [];
  return list.includes(id) ? list.filter((r) => r !== id) : [...list, id];
}

/**
 * Controlled request form shared by the new-request and fix-and-resubmit (edit)
 * views. Renders the role picker, the conditional division-home select,
 * justification, and the risk selector. The parent owns the submit / resubmit
 * button and any attestation gating.
 *
 * @param {object}   props
 * @param {object}   props.value     The request draft.
 * @param {Function} props.onChange  (nextDraft) => void
 */
export function AccessRequestForm({ value, onChange }) {
  const draft = value || {};
  const roleIds = Array.isArray(draft.requestedRoleIds) ? draft.requestedRoleIds : [];

  const rolesApi = useApi('/rbac/roles');
  const orgsApi = useApi('/organizations', { query: { limit: 100 } });
  const roles = rolesApi.data?.data || [];
  const orgs = orgsApi.data?.data || [];

  const divisionOrgs = orgs.filter(
    (o) => o?.type === 'division' && !STRATEGIC_ORG_PATTERN.test(o?.name || ''),
  );
  const showDivisionHome = rolesTriggerDivision(roleIds);

  const riskTone = RISK_TONE[draft.riskLevel] || 'slate';

  function patch(next) {
    onChange({ ...draft, ...next });
  }

  function onToggleRole(id) {
    patch({ requestedRoleIds: toggleRole(roleIds, id) });
  }

  function onSelectDivision(orgId) {
    if (!orgId) {
      patch({ requestedHomeOrganizationId: undefined, requestedLandingRoute: undefined });
      return;
    }
    patch({
      requestedHomeOrganizationId: orgId,
      requestedLandingRoute: '/loe-dashboard',
      requestedScopes: [{ organizationIds: [orgId] }],
    });
  }

  return (
    <div className="access-form">
      <fieldset className="access-role-picker">
        <legend className="access-form__legend">Roles requested</legend>
        <p className="access-form__hint">Pick the role(s) you need. Each reviewer assesses the fit.</p>
        {rolesApi.loading ? (
          <p className="access-form__hint">Loading roles…</p>
        ) : (
          <ul className="access-role-list">
            {roles.map((role) => {
              const checked = roleIds.includes(role.id);
              return (
                <li key={role.id} className={`access-role-item${checked ? ' is-selected' : ''}`}>
                  <label className="access-role-item__label">
                    <input
                      type="checkbox"
                      aria-label={role.name || role.id}
                      checked={checked}
                      onChange={() => onToggleRole(role.id)}
                    />
                    <span className="access-role-item__text">
                      <span className="access-role-item__name">{role.name || role.id}</span>
                      {role.description ? (
                        <span className="access-role-item__desc">{role.description}</span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </fieldset>

      {showDivisionHome ? (
        <label className="access-form__field">
          Which division will you lead?
          <span className="access-form__hint">Your board opens scoped to it.</span>
          <select
            value={draft.requestedHomeOrganizationId || ''}
            onChange={(e) => onSelectDivision(e.target.value)}
          >
            <option value="">Select a division…</option>
            {divisionOrgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label className="access-form__field">
        Justification
        <span className="access-form__hint">Explain the business need each reviewer will assess.</span>
        <textarea
          rows={4}
          value={draft.justification || ''}
          onChange={(e) => patch({ justification: e.target.value })}
        />
      </label>

      <div className="access-risk">
        <span className="access-form__field-label" id="access-risk-label">
          Risk level
        </span>
        <div className="access-risk__options" role="group" aria-labelledby="access-risk-label">
          {RISK_OPTIONS.map((opt) => {
            const tone = RISK_TONE[opt.value] || 'slate';
            const selected = (draft.riskLevel || 'standard') === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`access-risk__opt tone-${tone}`}
                aria-pressed={selected}
                onClick={() => patch({ riskLevel: opt.value })}
              >
                <span className="access-risk__opt-name">{opt.value}</span>
                <span className="access-risk__opt-sub">{opt.sub}</span>
              </button>
            );
          })}
        </div>
        <span className={`access-risk__consequence tone-${riskTone}`} aria-hidden style={{ display: 'none' }} />
      </div>
    </div>
  );
}
